// Integration connections: a workspace's Jira and Slack (#492), and its webhook
// receivers (#1073).
//
// Read off `routes/integrations.py`. The credential is sent once, on create, and
// never comes back: a read says `has_credential` and nothing else. What each kind
// takes is decided on #265 (C8-6, C8-7) and #266 (C9-1); the server validates it.
// A webhook receiver sends no credential at all: the server makes its signing secret
// and answers it once, on the create (#1072).

import type { Api } from "./http";
import { mintStepUpGrant } from "./passkeys";
import type { StepUp } from "./policy";

export const INTEGRATIONS_PATH = "/api/v1/integrations";

/** The kinds an adapter of ours sends to (#265, #266): what the first-run step offers. */
export type AdapterKind = "jira" | "slack";
export type IntegrationKind = AdapterKind | "webhook";

/** The alert severities, lowest first: a Slack connection's floor is one of them. */
export const SEVERITIES = ["info", "low", "medium", "high", "critical"] as const;
export type Severity = (typeof SEVERITIES)[number];

/**
 * A severity as a person reads it (#578). The wire value is unchanged: only the
 * option a person picks from and the line the list prints are capitalised.
 */
export const SEVERITY_LABEL: Record<Severity, string> = {
  info: "Info",
  low: "Low",
  medium: "Medium",
  high: "High",
  critical: "Critical",
};

/**
 * A Slack conversation id: `C` for a channel, `G` for a legacy private group.
 * Slack's own ids are upper-case; a value that does not match this is a name,
 * which `chat.postMessage` answers with `channel_not_found` (#578).
 */
export const SLACK_CHANNEL_ID = /^[CG][A-Z0-9]{7,}$/;

export interface JiraConfig {
  base_url: string;
  project_key: string;
  issue_type: string;
  labels: string[];
}

export interface SlackConfig {
  channel: string;
  severity_floor: Severity;
}

/** A webhook receiver's two settings (#1070): where it is posted, and what it is sent. */
export interface WebhookConfig {
  url: string;
  /** Wire names from `WEBHOOK_EVENTS`: `audit.*`, or a public run event's name. */
  events: string[];
}

/** The audit category a receiver subscribes to (`integrations/events.py`, `AUDIT`). */
export const AUDIT_EVENTS = "audit.*";

/** One entry a receiver may subscribe to: its wire name, and what a person reads. */
export interface WebhookEvent {
  wire: string;
  words: string;
}

/**
 * What a receiver may subscribe to, grouped as the form offers it (#1073): the audit log,
 * then the ten public run events (`integrations/events.py`, `SUBSCRIBABLE`). The wire names
 * are the server's; the words and the groups are only what a person reads.
 */
export const WEBHOOK_EVENTS: ReadonlyArray<{ group: string; events: ReadonlyArray<WebhookEvent> }> = [
  { group: "Audit log", events: [{ wire: AUDIT_EVENTS, words: "Every audit log entry" }] },
  {
    group: "Investigations",
    events: [
      { wire: "investigation.completed", words: "Investigation completed" },
      { wire: "investigation.failed", words: "Investigation failed" },
    ],
  },
  {
    group: "Changes",
    events: [
      { wire: "change.proposed", words: "Change proposed" },
      { wire: "change.approved", words: "Change approved" },
      { wire: "change.declined", words: "Change declined" },
      { wire: "change.expired", words: "Change expired" },
      { wire: "change.applied", words: "Change applied" },
      { wire: "change.verified", words: "Change verified" },
      { wire: "change.failed", words: "Change failed" },
    ],
  },
  { group: "Outcome", events: [{ wire: "outcome.observed", words: "Outcome observed" }] },
];

const EVENT_WORDS: ReadonlyMap<string, string> = new Map(
  WEBHOOK_EVENTS.flatMap(({ events }) => events.map(({ wire, words }) => [wire, words] as const)),
);

/** What a subscribed entry is called; an entry this console does not know, as it is. */
export const webhookEventInWords = (wire: string): string => EVENT_WORDS.get(wire) ?? wire;

/** An audit row's event on the wire is its action behind this prefix (`audit/export.py`). */
const AUDIT_EVENT_PREFIX = "audit.";

/**
 * A webhook delivery's event, as the receiver was sent it (#1073). A run event's row names
 * its run and carries the public name it was sent under; an audit row names no run and
 * carries the audit action, which goes out behind `audit.`. The two are told apart by the
 * run and not by the name, because an audit action may share one with a run event
 * (`audit/export.py`, `AUDIT_EVENT_PREFIX`).
 */
export function webhookDeliveryEvent(delivery: Pick<DeliveryRow, "event_type" | "run_id">): WebhookEvent | null {
  const type = delivery.event_type;
  if (typeof type !== "string" || type === "") return null;
  if (typeof delivery.run_id === "string" && delivery.run_id !== "") return { wire: type, words: webhookEventInWords(type) };
  return { wire: `${AUDIT_EVENT_PREFIX}${type}`, words: "Audit log entry" };
}

/**
 * Every state an `integration_delivery` row may hold (`integrations/delivery.py`).
 * `pending` is the one that reads wrong at a glance: it is a row nothing has attempted
 * *yet*, which since #577 includes one deliberately waiting its turn — not a failure,
 * and not "never used".
 */
export type DeliveryStatus = "pending" | "delivered" | "failed" | "abandoned" | "skipped";

/**
 * One delivery, as `GET /api/v1/integrations/{id}/deliveries` answers it (#579).
 *
 * The four row columns, the event in words, and the allow-listed part of the row's
 * `response` — the same allow-list `worker delivery-status` prints
 * (`integrations/delivery.py`, `DELIVERY_STATUS_KEYS`). Never the connection's config
 * and never its credential: neither is on the row, and neither is on this shape.
 */
export interface DeliveryRow {
  id: string;
  /** The investigation the delivery is of (#818); null on a row no run sent. Absent before #818. */
  run_id?: string | null;
  status: DeliveryStatus;
  attempts: number;
  updated_at: string;
  /** The event in words — "conclusion", "approval request" — or the raw type. */
  event: string | null;
  event_type?: string;
  /** The HTTP status the adapter recorded. `null` for a transport-level refusal. */
  last_status?: number | null;
  /** The adapter's own error text. Absent on a row nothing has attempted. */
  last_detail?: string | null;
  reason?: string;
  ceiling?: number;
  ticket?: { operation?: string; key?: string; url?: string; issue_id?: string; comment_id?: string };
  slack?: { channel?: string; ts?: string };
}

/**
 * What the check on save said (#598), as the API answers it on `last_check`.
 *
 * Asked of Jira or Slack straight after a save, off the request path, so a save's own
 * answer never carries one: it is `null` until the check has answered. `last_status`
 * and `last_detail` are a delivery's two fields under a delivery's two names, because
 * the same adapters wrote them and `connectionHealth.ts` reads both through one mapping.
 */
export interface CheckRow {
  status: "passed" | "failed";
  last_status: number | null;
  last_detail: string;
  checked_at: string;
  /** Present when this is not a check's answer but a test delivery's (#614): something was sent. */
  what?: "test_delivery";
  /**
   * The Slack scopes a check found missing (#605), comma-separated as Slack names them:
   * `integrations/check.py`'s `scope_finding`. Kept beside a test delivery's answer,
   * which reads no scope, and set or cleared only by a check that read the channel.
   * Absent where nothing is missing, or on a row checked before it was kept.
   */
  needs?: string;
}

interface Row<K extends IntegrationKind, C> {
  id: string;
  kind: K;
  config: C;
  has_credential: boolean;
  active: boolean;
  created_at: string;
  updated_at: string;
  disabled_at: string | null;
  /** The newest delivery for this connection, or null when there has never been one. */
  last_delivery: DeliveryRow | null;
  /** The newest check's answer — on save (#598) or from Test connection (#603) — or null or absent while none has answered. */
  last_check?: CheckRow | null;
  /**
   * Which credential it holds (#936), never the credential: a pasted token, or the Slack
   * workspace's installation, Add to Slack's. Null on a row with none; absent before #936.
   */
  credential_kind?: "pasted" | "installation" | "generated" | null;
  /** The Slack workspace's name, on a connection that posts with its installation (#936). */
  team_name?: string | null;
  /** The installation it posts through (#938): what Reinstall replaces. Null on a pasted one. */
  installation_id?: string | null;
  /** What Slack granted that installation (#938), never its token. Null on a pasted one. */
  installation_scopes?: string[] | null;
  /** When Slack removed that installation (`app_uninstalled`, #938), or null while it is live. */
  installation_removed_at?: string | null;
  /**
   * A webhook receiver's generated secret's first characters (#1072), never the secret: null
   * on a receiver whose secret was typed before secrets were generated, and on every other kind.
   */
  secret_prefix?: string | null;
  /**
   * When the secret a rotation replaced stops signing beside the current one (#1075), or null
   * when no overlap is running. Read without the secret, as the prefix is.
   */
  previous_secret_until?: string | null;
}

export type IntegrationRow = Row<"jira", JiraConfig> | Row<"slack", SlackConfig> | Row<"webhook", WebhookConfig>;

export type JiraRow = Row<"jira", JiraConfig>;
export type SlackRow = Row<"slack", SlackConfig>;
export type WebhookRow = Row<"webhook", WebhookConfig>;

/** The create's answer for a receiver (#1072): the one answer that carries its `secret`. */
export type CreatedWebhook = WebhookRow & { secret: string };

/**
 * A rotation's answer (#1075): the one answer that carries the new `secret`, and whether a
 * secret older than the one replaced stopped signing at once.
 */
export type RotatedWebhook = WebhookRow & { secret: string; earlier_secret_stopped_signing: boolean };

export type NewJira = {
  kind: "jira";
  config: JiraConfig;
  credential: JiraCredential;
  /**
   * A second connection to a site and space an active one already has, meant (#653).
   * Sent only after the server has refused one without it and a person has said so.
   */
  alongside_existing?: true;
};
/** A Slack connection that posts with a pasted bot token: your own Slack app's. */
export type NewPastedSlack = {
  kind: "slack";
  config: SlackConfig;
  credential: SlackCredential;
  /** A second connection to a channel an active one in the same Slack workspace posts to, meant (#762): `NewJira`'s. */
  alongside_existing?: true;
};
/**
 * A Slack connection that posts with the workspace's installation (#936): Add to Slack's
 * (#937). The browser never holds the token; it names the installation the callback
 * returned with.
 */
export type NewInstalledSlack = {
  kind: "slack";
  config: SlackConfig;
  installation_id: string;
  alongside_existing?: true;
};
export type NewSlack = NewPastedSlack | NewInstalledSlack;
/**
 * A webhook receiver (#1072): its settings and a `webhook_create` grant, and **no
 * credential**, which the server makes. A body carrying one is refused.
 */
export type NewWebhook = {
  kind: "webhook";
  config: WebhookConfig;
  step_up_grant: string;
};
export type NewIntegration = NewJira | NewSlack | NewWebhook;

/** The step-up purpose registering a receiver is proven for (`setup.WEBHOOK_CREATE_PURPOSE`). */
export const WEBHOOK_CREATE_PURPOSE = "webhook_create";

/**
 * How long the secret a rotation replaces goes on signing (`setup.SECRET_OVERLAP`, #1075): what
 * the confirmation tells a person before they rotate.
 */
export const SECRET_OVERLAP_HOURS = 24;

/** The step-up purpose rotating a receiver's secret is proven for (`setup.WEBHOOK_ROTATE_PURPOSE`). */
export const WEBHOOK_ROTATE_PURPOSE = "webhook_rotate";

export interface JiraCredential {
  email: string;
  api_token: string;
}
export interface SlackCredential {
  bot_token: string;
}

/**
 * What an edit sends (#580): the keys that changed, and no others.
 *
 * **A key left out is a key left alone**, which the server does with `coalesce`
 * (`integrations/setup.py`, `update`). That is the whole shape of this type: a
 * `credential` absent is the token already sealed, kept; a `config` absent is the
 * connection's settings, kept. The console's rule — an unchanged token is never
 * re-sent — is enforced by never putting the key there, not by sending a value
 * the server has to recognise as "the same one".
 *
 * `config`, when it is sent, is the **whole** config for that kind: the server
 * validates all of C8-7's or C9-1's settings together and replaces the lot, so
 * there is no such thing as a half config on the wire.
 */
export type JiraPatch = {
  config?: JiraConfig;
  credential?: JiraCredential;
  /** A move onto a site and space an active connection already has, meant (#653): `NewJira`'s. */
  alongside_existing?: true;
};
export type SlackPatch = {
  config?: SlackConfig;
  credential?: SlackCredential;
  /** A move onto a channel an active connection already posts to, meant (#762): `NewJira`'s. */
  alongside_existing?: true;
};
/** A receiver's edit (#1073): the URL and the events, whole. Never a secret: the server's. */
export type WebhookPatch = {
  config?: WebhookConfig;
};
export type IntegrationPatch = JiraPatch | SlackPatch | WebhookPatch;

export const listIntegrations = (api: Api): Promise<IntegrationRow[]> =>
  api.get<IntegrationRow[]>(INTEGRATIONS_PATH);

export const createIntegration = (api: Api, body: NewIntegration): Promise<IntegrationRow> =>
  api.post<IntegrationRow>(INTEGRATIONS_PATH, body);

/**
 * Register a receiver (#1072, #1073): `POST /auth/step-up` for `webhook_create`, then the
 * create with that grant. The answer is the only one that carries the secret; the caller
 * shows it once and keeps it nowhere.
 */
export async function registerWebhook(api: Api, config: WebhookConfig, stepUp: StepUp): Promise<CreatedWebhook> {
  if (stepUp.credential === "") throw new Error("Registering a webhook needs you to confirm it is you.");
  const grant = await mintStepUpGrant(api, stepUp, WEBHOOK_CREATE_PURPOSE);
  const body: NewWebhook = { kind: "webhook", config: { url: config.url, events: [...config.events] }, step_up_grant: grant };
  return api.post<CreatedWebhook>(INTEGRATIONS_PATH, body);
}

/** Where a receiver's secret is rotated (#1075). */
export const rotateSecretPath = (id: string): string => `${INTEGRATIONS_PATH}/${encodeURIComponent(id)}/rotate-secret`;

/**
 * Rotate a receiver's secret (#1075): `POST /auth/step-up` for `webhook_rotate`, then the
 * rotation with that grant. The answer is the only one that carries the new secret; the
 * caller shows it once and keeps it nowhere.
 */
export async function rotateWebhookSecret(api: Api, id: string, stepUp: StepUp): Promise<RotatedWebhook> {
  if (stepUp.credential === "") throw new Error("Rotating a webhook's secret needs you to confirm it is you.");
  const grant = await mintStepUpGrant(api, stepUp, WEBHOOK_ROTATE_PURPOSE);
  return api.post<RotatedWebhook>(rotateSecretPath(id), { step_up_grant: grant });
}

export const disableIntegration = (api: Api, id: string): Promise<IntegrationRow> =>
  api.post<IntegrationRow>(`${INTEGRATIONS_PATH}/${encodeURIComponent(id)}/disable`);

/**
 * Check a disconnected connection again; it goes live only if the check passes (#820). The
 * row as it stands when the check starts — still disconnected — like a test's answer.
 */
export const enableIntegration = (api: Api, id: string): Promise<IntegrationRow> =>
  api.post<IntegrationRow>(`${INTEGRATIONS_PATH}/${encodeURIComponent(id)}/enable`);

/** Remove a connection's saved token for good, and the connection from the list (#820). */
export const deleteIntegration = (api: Api, id: string): Promise<void> =>
  api.delete<void>(`${INTEGRATIONS_PATH}/${encodeURIComponent(id)}`);

/** Change a live connection instead of disconnecting and re-creating it (#580). */
export const updateIntegration = (
  api: Api,
  id: string,
  body: IntegrationPatch,
): Promise<IntegrationRow> =>
  api.patch<IntegrationRow>(`${INTEGRATIONS_PATH}/${encodeURIComponent(id)}`, body);

/** One connection, as the list carries it: what a test's outcome is read from (#603). */
export const readIntegration = (api: Api, id: string): Promise<IntegrationRow> =>
  api.get<IntegrationRow>(`${INTEGRATIONS_PATH}/${encodeURIComponent(id)}`);

/**
 * Ask Jira or Slack again with the credential the connection already holds (#603).
 * No body: there is no credential to send. The answer is the connection as it stands;
 * the outcome lands on its `last_check` afterwards (`console/connectionTest.ts`).
 */
export const testIntegration = (api: Api, id: string): Promise<IntegrationRow> =>
  api.post<IntegrationRow>(`${INTEGRATIONS_PATH}/${encodeURIComponent(id)}/test`);

/**
 * Send one work item or message, marked as a test, from a saved connection (#614).
 * No body, as for `testIntegration`. The answer is the connection as it stands; what the
 * far side said lands on its `last_check` afterwards, with `what: "test_delivery"`.
 */
export const sendTestDelivery = (api: Api, id: string): Promise<IntegrationRow> =>
  api.post<IntegrationRow>(`${INTEGRATIONS_PATH}/${encodeURIComponent(id)}/test-delivery`);

/** Where one connection's deliveries are read from (#579). */
export const deliveriesPath = (id: string): string =>
  `${INTEGRATIONS_PATH}/${encodeURIComponent(id)}/deliveries`;

export const listIntegrationDeliveries = (api: Api, id: string): Promise<DeliveryRow[]> =>
  api.get<DeliveryRow[]>(deliveriesPath(id));

/** The statuses a delivery may be retried from (#819): it did not go through. */
export const RETRYABLE: readonly DeliveryStatus[] = ["failed", "abandoned"];

/**
 * Put one failed or abandoned delivery back on the queue (#819). No body. The answer is
 * the row as it now stands, `pending`, in the shape the deliveries read answers; a row
 * that went through, is already queued or was skipped is refused with a sentence to show.
 */
export const retryDelivery = (api: Api, connectionId: string, deliveryId: string): Promise<DeliveryRow> =>
  api.post<DeliveryRow>(`${deliveriesPath(connectionId)}/${encodeURIComponent(deliveryId)}/retry`);

/**
 * Where a connection sends, as a person reads it (#578).
 *
 * Jira reads as the work item key's prefix, the work type, and the site it is
 * on — `TAT · Task on rochwall.atlassian.net` — because that is the order a
 * person checks it in. Slack prints the channel, and what the floor gates: only
 * conclusions, never an approval request (`integrations/slack.py`, `_plan`).
 */
export function targetOf(row: IntegrationRow): string {
  if (row.kind === "jira") return jiraTargetOf(row.config);
  if (row.kind === "slack") {
    return `${slackChannelShown(row.config.channel)} · conclusions at ${SEVERITY_LABEL[row.config.severity_floor]} and above`;
  }
  // A webhook receiver, by its URL (#637, #1073), and any kind these types do not name that
  // the list still answers. Read as a Slack row, it threw and took the list with it.
  const url = (row as { config?: { url?: unknown } }).config?.url;
  return typeof url === "string" ? url : "";
}

/** A stored channel as a person reads it: an ID as it is, and a name from before #571 with its `#`. */
export function slackChannelShown(channel: string): string {
  return SLACK_CHANNEL_ID.test(channel) ? channel : `#${channel.replace(/^#/, "")}`;
}

/** `targetOf` for a Jira config alone: what a refusal naming another connection carries (#653). */
export function jiraTargetOf(config: JiraConfig): string {
  const site = config.base_url.replace(/^https?:\/\//, "").replace(/\/$/, "");
  const labels = config.labels.length === 0 ? "" : ` · labels ${config.labels.join(", ")}`;
  return `${config.project_key} · ${config.issue_type} on ${site}${labels}`;
}
